---
title: "Sending outreach without burning your domain"
description: "How Chosen sends sourcing outreach safely — paced sends, automatic per-domain rate limits, and what happens when a prospect's email isn't known yet."
category: "sourcing"
keywords:
  - "send outreach"
  - "outreach rate limits"
  - "email deliverability recruiting"
  - "domain reputation"
  - "cold email sending"
schema: "HowTo"
nav:
  section: "guides"
  order: 40
  parent: "/guides/sourcing"
---

Once a [draft](/guides/sourcing/outreach-drafts) is ready, sending it is one click. If the prospect's email is confirmed, the message goes out now. If it isn't, the message queues and sends itself once [enrichment](/guides/sourcing/enrichment) resolves the address. Underneath, Chosen paces sends and caps them per domain so your outreach doesn't trip a company's spam tooling.

## Send or queue

The composer picks the action for you, based on whether the prospect has a confirmed email:

- **Send.** The email is known, so the message goes out now and threads into your inbox.
- **Get email & send.** No confirmed email yet. Chosen queues the draft, runs enrichment, and sends the moment an address resolves — nothing more for you to do.

Either way the message sends from your own connected [Gmail](/getting-started/integrations/gmail), so replies come back to you.

## Sends are paced

Chosen doesn't fire everything at once. When you send to several prospects, the messages are spaced out over time rather than leaving in one burst. A stack of near-identical emails leaving one inbox in one minute is the clearest possible signal to a spam filter that something automated is happening. Spacing them out keeps your inbox looking like an inbox.

## Per-domain limits are automatic

When several prospects work at the same company, Chosen caps how fast it emails that domain. The limits apply on their own — there's nothing to configure:

- At most **5 sends per hour** to any single recipient domain.
- At most **50 sends per day** to any single recipient domain.

This avoids a specific failure: email five people at one company inside a minute and you can trip that company's security tooling, which then flags everything from your domain — including the messages that mattered. The per-domain cap paces those sends so they read as ordinary email.

## When a prospect replies

A reply threads straight back to your connected inbox, and Chosen keeps it attached to the prospect's record so the conversation doesn't get lost. Before you send anyone a follow-up, check whether they already wrote back — a "just checking in" to someone who replied last week is the fastest way to look like a bot.

<Callout type="warning" title="Don't send one generic message to everyone">
The urge to send a single "still interested?" note to every prospect at once is strong, and it backfires. Reply rates crater, and your domain reputation takes a hit that costs you on every email afterward — including offers and interview scheduling. Send to people the message genuinely fits. A sharp message to 80 of the right prospects beats a vague one to 400.
</Callout>

## When it gets it wrong

Chosen's pacing and per-domain caps protect your domain, but they can't rescue a generic message sent too widely — that hurts deliverability no matter how carefully the sends are spaced. And sending depends on [Gmail](/getting-started/integrations/gmail) staying connected: if it disconnects, queued outreach waits until you reconnect it.

A prospect who's gone quiet can be picked up again later — [search your prospect pool](/guides/sourcing/find-prospects) to resurface them. And when a prospect replies and wants to talk, [convert them into a candidate](/guides/sourcing/prospect-to-candidate).
